import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"]

export default class extends Controller {
  static targets = [
    "fileInput",
    "preview",
    "photoBlock",
    "slot",
    "existingActions",
    "addBtn",
    "uploadModal",
    "uploadModalContent",
    "uploadPreview",
    "uploadPreviewImage",
    "uploadPreviewMeta",
    "uploadActions",
    "dropzone",
    "removeModal",
    "removePreviewImage",
    "removePreviewMeta",
    "confirmBtn",
    "successToast",
    "errorToast"
  ]

  static values = {
    hasLogo: Boolean,
    existingLogoUrl: String,
    existingLogoName: String,
    existingLogoSize: Number,
    uploadUrl: String,
    removeUrl: String,
    maxBytes: { type: Number, default: 5 * 1024 * 1024 },
    minHeight: { type: Number, default: 44 }
  }

  connect() {
    if (this.hasUploadModalTarget) {
      this._uploadModal = new Modal(this.uploadModalTarget)
    }

    if (this.hasRemoveModalTarget) {
      this._removeModal = new Modal(this.removeModalTarget)
    }
  }

  disconnect() {
    this._revokePendingPreviewUrl()
    this._uploadModal?.dispose()
    this._removeModal?.dispose()
  }

  openUploadModal(event) {
    event?.preventDefault()
    this._uploadConfirmed = false
    this._showUploadEmptyState()
    this._uploadModal?.show()
  }

  openUploadModalFromKeyboard(event) {
    if (event.key !== "Enter" && event.key !== " ") return

    event.preventDefault()
    this.openUploadModal(event)
  }

  openFilePicker(event) {
    event?.preventDefault()
    this.fileInputTarget.click()
  }

  closeUploadModal(event) {
    event?.preventDefault()

    if (!this._uploadConfirmed) {
      this._resetFileInput()
      this._revokePendingPreviewUrl()
    }

    this._showUploadEmptyState()
    this._uploadModal?.hide()
  }

  async confirmUpload(event) {
    event?.preventDefault()

    const file = this.fileInputTarget.files?.[0]
    if (!file) {
      this._uploadModal?.hide()
      return
    }

    await this._saveLogoToServer(file)
  }

  changeUpload(event) {
    event?.preventDefault()
    this._resetFileInput()
    this._revokePendingPreviewUrl()
    this._showUploadEmptyState()
    this.openFilePicker()
  }

  handleFileChange(event) {
    const file = event.target.files?.[0]
    if (file) this._processFile(file)
  }

  dragOver(event) {
    event.preventDefault()
  }

  dragEnter(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.add("is-drag-over")
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget && !this.dropzoneTarget.contains(event.relatedTarget)) {
      this.dropzoneTarget.classList.remove("is-drag-over")
    }
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.remove("is-drag-over")

    const file = event.dataTransfer.files?.[0]
    if (file) this._processFile(file)
  }

  openRemoveModal(event) {
    event?.preventDefault()
    if (!this.hasLogoValue || !this.hasRemoveModalTarget) return

    this._populateRemoveModalPreview()
    this._removeModal.show()
  }

  closeRemoveModal(event) {
    event?.preventDefault()
    this._removeModal?.hide()
  }

  async confirmRemove(event) {
    event?.preventDefault()
    await this._removeLogoFromServer()
  }

  _processFile(file) {
    const error = this._validateFile(file)
    if (error) {
      this._resetFileInput()
      this._showSnackbar("error", error)
      return
    }

    if (this._isRaster(file)) {
      this._validateRasterHeight(file)
      return
    }

    this._showPendingPreview(file)
  }

  _validateFile(file) {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Use a PNG, JPG, or SVG."
    }

    if (file.size > this.maxBytesValue) {
      return "Logo must be 5 MB or smaller."
    }

    return null
  }

  _isRaster(file) {
    return file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg"
  }

  _validateRasterHeight(file) {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)

      if (image.naturalHeight < this.minHeightValue) {
        this._resetFileInput()
        this._showSnackbar("error", "Logo must be at least 44px tall.")
        return
      }

      this._showPendingPreview(file)
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      this._resetFileInput()
      this._showSnackbar("error", "This file could not be read as an image.")
    }

    image.src = objectUrl
  }

  _showPendingPreview(file) {
    const dt = new DataTransfer()
    dt.items.add(file)
    this.fileInputTarget.files = dt.files

    this._revokePendingPreviewUrl()
    this._pendingPreviewUrl = URL.createObjectURL(file)
    this.uploadPreviewImageTarget.src = this._pendingPreviewUrl
    this.uploadPreviewMetaTarget.textContent = file.name
    this._showUploadPreviewState()
  }

  async _saveLogoToServer(file) {
    if (this.hasConfirmBtnTarget) {
      this.confirmBtnTarget.disabled = true
    }

    const formData = new FormData()
    formData.append("company[logo]", file)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "PATCH",
        body: formData,
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this._setPreviewFromUrl(data.logo_url)
        this.hasLogoValue = true
        this.existingLogoUrlValue = data.logo_url
        this.existingLogoNameValue = data.filename
        this.existingLogoSizeValue = data.byte_size
        this._showHasLogoState()
        this._resetFileInput()
        this._revokePendingPreviewUrl()
        this._uploadConfirmed = true
        this._showUploadEmptyState()
        this._uploadModal?.hide()
        this._showSnackbar("success", data.message)
      } else {
        this._showSnackbar("error", data.errors?.join(", ") || data.message)
      }
    } catch (_error) {
      this._showSnackbar("error", "Logo could not be saved.")
    } finally {
      if (this.hasConfirmBtnTarget) {
        this.confirmBtnTarget.disabled = false
      }
    }
  }

  async _removeLogoFromServer() {
    try {
      const response = await fetch(this.removeUrlValue, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this._applyLogoRemoval()
        this._removeModal?.hide()
        this._showSnackbar("success", data.message)
      } else {
        this._showSnackbar("error", data.errors?.join(", ") || data.message)
      }
    } catch (_error) {
      this._showSnackbar("error", "Logo could not be removed.")
    }
  }

  _applyLogoRemoval() {
    this._resetFileInput()
    this._revokePendingPreviewUrl()
    this.hasLogoValue = false
    this.existingLogoUrlValue = ""
    this.existingLogoNameValue = ""
    this.existingLogoSizeValue = 0
    this.previewTarget.innerHTML = ""
    this._showEmptyLogoState()
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  _showSnackbar(type, title) {
    const template = type === "error"
      ? (this.hasErrorToastTarget ? this.errorToastTarget : null)
      : (this.hasSuccessToastTarget ? this.successToastTarget : null)

    if (!template) return

    const node = template.content.firstElementChild.cloneNode(true)
    const titleEl = node.querySelector(".snackbar-title span")
    if (titleEl) titleEl.textContent = title

    this._flashContainer().appendChild(node)
  }

  _flashContainer() {
    let container = document.querySelector(".app-flash-alert-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "app-flash-alert-container"
      document.body.appendChild(container)
    }
    return container
  }

  _showUploadEmptyState() {
    if (!this.hasUploadModalContentTarget) return

    this.uploadModalContentTarget.classList.remove("is-preview")

    if (this.hasDropzoneTarget) this.dropzoneTarget.hidden = false
    if (this.hasUploadPreviewTarget) this.uploadPreviewTarget.hidden = true
    if (this.hasUploadActionsTarget) this.uploadActionsTarget.hidden = true
  }

  _showUploadPreviewState() {
    if (!this.hasUploadModalContentTarget) return

    this.uploadModalContentTarget.classList.add("is-preview")

    if (this.hasDropzoneTarget) this.dropzoneTarget.hidden = true
    if (this.hasUploadPreviewTarget) this.uploadPreviewTarget.hidden = false
    if (this.hasUploadActionsTarget) this.uploadActionsTarget.hidden = false
  }

  _revokePendingPreviewUrl() {
    if (this._pendingPreviewUrl) {
      URL.revokeObjectURL(this._pendingPreviewUrl)
      this._pendingPreviewUrl = null
    }
  }

  _populateRemoveModalPreview() {
    const { url, name } = this._logoPreviewDetails()
    this.removePreviewImageTarget.src = url
    this.removePreviewMetaTarget.textContent = name
  }

  _logoPreviewDetails() {
    const previewImg = this.previewTarget.querySelector("img")
    const url = previewImg?.currentSrc || previewImg?.src || this.existingLogoUrlValue
    const name = this.existingLogoNameValue || previewImg?.alt || "logo.png"

    return { url, name }
  }

  _showHasLogoState() {
    if (!this.hasPhotoBlockTarget) return

    this.photoBlockTarget.classList.add("hub-company-logo--has-logo")
    this._makeSlotStatic()

    if (this.hasAddBtnTarget) this.addBtnTarget.hidden = true
    if (this.hasExistingActionsTarget) this.existingActionsTarget.hidden = false
  }

  _showEmptyLogoState() {
    if (!this.hasPhotoBlockTarget) return

    this.photoBlockTarget.classList.remove("hub-company-logo--has-logo")
    this._makeSlotInteractive()

    if (this.hasAddBtnTarget) this.addBtnTarget.hidden = false
    if (this.hasExistingActionsTarget) this.existingActionsTarget.hidden = true
  }

  _makeSlotStatic() {
    if (!this.hasSlotTarget) return

    this.slotTarget.classList.remove("hub-company-logo-slot--interactive")
    this.slotTarget.removeAttribute("data-action")
    this.slotTarget.removeAttribute("role")
    this.slotTarget.removeAttribute("tabindex")
    this.slotTarget.removeAttribute("aria-label")
  }

  _makeSlotInteractive() {
    if (!this.hasSlotTarget) return

    this.slotTarget.classList.add("hub-company-logo-slot--interactive")
    this.slotTarget.dataset.action = "click->company-logo-upload#openUploadModal keydown->company-logo-upload#openUploadModalFromKeyboard"
    this.slotTarget.setAttribute("role", "button")
    this.slotTarget.setAttribute("tabindex", "0")
    this.slotTarget.setAttribute("aria-label", "Add logo")
  }

  _setPreviewFromUrl(url) {
    this.previewTarget.innerHTML = ""
    const img = document.createElement("img")
    img.src = url
    img.alt = "Company logo"
    img.className = this.previewTarget.dataset.imageClass || "hub-company-logo-image"
    this.previewTarget.appendChild(img)
  }

  _resetFileInput() {
    this.fileInputTarget.value = ""
  }
}
